// @givmo/app-admin — Impact panel: "Top charities (YTD)" + the charities-supported header stat (§133).
//
// The charities the company's employees most supported, by total. Spec §133 gives this panel two
// additions on top of the ranked list it already had:
//   - `charities_supported_ytd` as its HEADER STAT — the card the money-strip re-rank displaced (§129),
//     re-homed rather than removed;
//   - the recap CTA into /reports, which owns the year's artifacts.
//
// BASIS: charities-supported is the UNION count — distinct charities reached through matched requests AND
// the company's own direct gifts. The EP-WEB-B1 split deliberately left it alone (unlike the "matched"
// dollar figures) because "charities supported" is honest across both legs. The ranked rows below carry
// the same union basis, which is why the subtitle says "supported" rather than "matched".
//
// THE CTA NAVIGATES, IT DOES NOT DOWNLOAD. /reports is where the recap and the CSVs live (EP-WEB7), so
// this control's promise is exactly "take me there" — it must not be relabelled as a download trigger
// while the file it would name is produced by another surface.
//
// PURELY PRESENTATIONAL BY DESIGN: the page owns the query and hands this section the resolved rows.
// Amounts render through `formatUsd` — formatted, never recomputed.
//
// THE PRIVACY DOOR (EP-MNT5(c), D-EP-48 ruling 3): where the host has one and the person may use it,
// the card's foot carries a quiet link onto the Company visibility settings — this is the
// card that shows what the company's giving looks like, which is when "who can see this?" comes to
// mind. The page decides whether there is a door; this section only draws it.

import { Link } from '@tanstack/react-router';
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  EmptyState,
  Icon,
  Skeleton,
  testIdProp,
} from '@givmo/design-system';
import { ArrowRight, FileText, HeartOff } from 'lucide-react';
import type { CompanyTopCharity } from '@givmo/api-client';
import { MethodologyHint } from '../../../components/methodology-hint';
import type { EmployerModuleAnchoredDoor } from '../../../lib/module-paths';
import { formatCount, formatUsd } from '../dashboard-decode';
import { CharityCell } from './charity-cell';
import { InlineError, errorMessage } from './section-status';

const CHARITIES_METHODOLOGY =
  'Distinct charities your company reached this calendar year to date (America/New_York), counting both ' +
  "employee match requests and your company's own direct gifts. A charity supported ten times counts " +
  'once. The rows below rank the same set by total dollars.';

export function TopCharitiesCard({
  rows,
  loading,
  isError,
  error,
  onRetry,
  charitiesSupported,
  onOpenRecap,
  privacyDoor = null,
}: {
  rows: CompanyTopCharity[];
  loading: boolean;
  isError: boolean;
  error: unknown;
  onRetry: () => void;
  /** `charities_supported_ytd` — undefined while the dashboard read is pending/failed (→ em-dash). */
  charitiesSupported: number | undefined;
  onOpenRecap: () => void;
  /** The contextual door onto the company's privacy settings, or `null` for none. */
  privacyDoor?: EmployerModuleAnchoredDoor | null;
}) {
  return (
    <Card className="flex flex-col">
      <CardHeader
        title="Impact"
        subtitle="Charities your company supported this year"
        actions={
          <Button
            appearance="saas"
            tone="ghost"
            size="sm"
            leadingIcon={<Icon icon={FileText} size={14} aria-hidden />}
            onClick={onOpenRecap}
            testId="impact-recap-cta"
          >
            This year&apos;s recap
          </Button>
        }
      />

      {/* The header stat: the displaced "Charities supported" card, re-homed as this panel's headline. */}
      <div className="flex flex-wrap items-baseline gap-8 border-b border-border-subtle px-20 py-16">
        <span className="font-sans text-s30 font-w700 leading-h1 tracking-givmo text-text-strong tabular-nums">
          {formatCount(charitiesSupported)}
        </span>
        <span className="flex items-center gap-6 font-sans text-s13 font-w500 tracking-normal text-text-tertiary">
          charities supported (YTD)
          <MethodologyHint subject="Charities supported" testId="impact-charities-methodology">
            {CHARITIES_METHODOLOGY}
          </MethodologyHint>
        </span>
      </div>

      <CardBody flush className="flex-1">
        {loading ? (
          <TopCharitiesSkeleton />
        ) : isError ? (
          <div className="px-20 py-16">
            <InlineError
              message={errorMessage(error, 'Failed to load top charities.')}
              onRetry={onRetry}
            />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Icon icon={HeartOff} size={20} aria-hidden />}
            title="No charity activity yet"
            description="The charities your employees support will rank here."
          />
        ) : (
          <ol className="flex flex-col">
            {rows.map((row, i) => (
              <li
                key={row.charity.id}
                className="flex items-center gap-12 border-b border-border-subtle px-20 py-12 last:border-b-0"
              >
                <span className="w-16 shrink-0 font-sans text-s13 font-w600 tracking-givmo text-text-tertiary tabular-nums">
                  {i + 1}
                </span>
                <div className="min-w-0 flex-1">
                  <CharityCell charity={row.charity} />
                </div>
                <div className="flex shrink-0 flex-col items-end gap-2">
                  <span className="font-sans text-s14 font-w600 tracking-givmo text-text-strong tabular-nums">
                    {formatUsd(row.total)}
                  </span>
                  <span className="font-sans text-s12 tracking-normal text-text-tertiary tabular-nums">
                    {row.count.toLocaleString('en-US')} {row.count === 1 ? 'donation' : 'donations'}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        )}
      </CardBody>

      {privacyDoor !== null && (
        <CardFooter>
          <Link
            to={privacyDoor.to}
            search={privacyDoor.search}
            hash={privacyDoor.hash}
            className="inline-flex items-center gap-4 font-sans text-s13 font-w600 tracking-givmo text-brand-600 outline-none hover:underline focus-visible:underline"
            {...testIdProp('impact-privacy-link')}
          >
            {privacyDoor.label}
            <Icon icon={ArrowRight} size={14} aria-hidden />
          </Link>
        </CardFooter>
      )}
    </Card>
  );
}

function TopCharitiesSkeleton() {
  return (
    <ul className="flex flex-col">
      {Array.from({ length: 5 }).map((_, i) => (
        <li
          key={i}
          className="flex items-center gap-12 border-b border-border-subtle px-20 py-12 last:border-b-0"
        >
          <Skeleton className="size-32 shrink-0 rounded-full" />
          <Skeleton className="h-12 flex-1" />
          <Skeleton className="h-12 w-16" />
        </li>
      ))}
    </ul>
  );
}
